import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useMemo } from "react";
import { setlistQuery } from "../lib/queries";

export type SetlistNavItem = {
  id: string;
  title: string;
  href: string;
} | null;

/** The page being shown: a song, or a setlist's title-only entry. */
export type SetlistNavCurrent = { songId: string } | { entryId: string };

/** Where a setlist entry's page lives: its song's page, or its own. */
export function setlistEntryHref(
  setlistId: string,
  entry: { id: string; song: { id: string } | null },
): string {
  return entry.song
    ? `/songs/${entry.song.id}?from=setlist-${setlistId}`
    : `/setlists/${setlistId}/entries/${entry.id}`;
}

/**
 * Back/prev/next navigation for a page opened from a setlist
 * (`?from=setlist-<id>`). Steps through every entry, including songs not
 * kept in Bandwith. Falls back to "All Songs" otherwise.
 */
export function useSetlistNavigation(
  current: SetlistNavCurrent,
  from: string | undefined,
) {
  const setlistMatch = from?.match(/^setlist-([a-zA-Z0-9-]+)$/);
  const setlistId = setlistMatch?.[1];
  // Primitives, so the memo below doesn't rerun for each new `current` object
  const songId = "songId" in current ? current.songId : undefined;
  const entryId = "entryId" in current ? current.entryId : undefined;

  const { data: setlist } = useQuery({
    ...setlistQuery(setlistId ?? ""),
    enabled: !!setlistId,
  });

  return useMemo(() => {
    if (!setlistId || !setlist) {
      return {
        href: "/",
        label: "All Songs",
        prev: null as SetlistNavItem,
        next: null as SetlistNavItem,
      };
    }

    const entries = setlist.songs;
    const currentIndex = entries.findIndex((e) =>
      songId ? e.song?.id === songId : e.id === entryId,
    );
    const toNavItem = (index: number): SetlistNavItem => {
      const entry = currentIndex >= 0 ? entries[index] : undefined;
      if (!entry) return null;
      return {
        id: entry.id,
        title: entry.song?.title ?? entry.title ?? "",
        href: setlistEntryHref(setlistId, entry),
      };
    };

    return {
      href: `/setlists/${setlistId}`,
      label: setlist.name,
      prev: toNavItem(currentIndex - 1),
      next: toNavItem(currentIndex + 1),
    };
  }, [setlistId, setlist, songId, entryId]);
}

/** Prev/next song links shown when navigating a song within a setlist. */
export function PrevNextNav({
  prev,
  next,
}: {
  prev: SetlistNavItem;
  next: SetlistNavItem;
}) {
  if (prev === null && next === null) return null;

  return (
    <div className="flex items-center justify-between">
      {prev ? (
        <Link
          to={prev.href}
          className="group inline-flex items-center gap-1 text-sm text-text-secondary transition-colors hover:text-amber"
        >
          <ChevronLeft
            className="h-4 w-4 transition-transform duration-150 group-hover:-translate-x-0.5"
            aria-hidden="true"
          />
          <span className="max-w-[120px] truncate sm:max-w-[180px]">
            {prev.title}
          </span>
        </Link>
      ) : (
        <span
          aria-disabled="true"
          className="inline-flex items-center gap-1 text-sm text-text-muted/50"
        >
          <ChevronLeft className="h-4 w-4" aria-hidden="true" />
          <span className="max-w-[120px] truncate sm:max-w-[180px]">
            [none]
          </span>
        </span>
      )}
      {next ? (
        <Link
          to={next.href}
          className="group inline-flex items-center gap-1 text-sm text-text-secondary transition-colors hover:text-amber"
        >
          <span className="max-w-[120px] truncate sm:max-w-[180px]">
            {next.title}
          </span>
          <ChevronRight
            className="h-4 w-4 transition-transform duration-150 group-hover:translate-x-0.5"
            aria-hidden="true"
          />
        </Link>
      ) : (
        <span
          aria-disabled="true"
          className="inline-flex items-center gap-1 text-sm text-text-muted/50"
        >
          <span className="max-w-[120px] truncate sm:max-w-[180px]">
            [none]
          </span>
          <ChevronRight className="h-4 w-4" aria-hidden="true" />
        </span>
      )}
    </div>
  );
}
